import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { UtilityListItem, UtilityRow } from '../types/utility';
import { UTILITY_LIST_COLUMNS } from '../types/utility';

/**
 * Reads of the utilities table.
 *
 * The table is national — about 3,400 rows — so the picker asks for relevance
 * rather than for everything: fetchUtilitiesForZip() and fetchUtilitiesForState()
 * for the likely answers, searchUtilitiesByName() for the rest. Between them a
 * contractor reaches any utility in the country without the client ever holding
 * the whole table.
 *
 * fetchAllUtilities() survives for callers that genuinely need every row. It
 * reads in pages because PostgREST caps a single response at 1,000 rows and
 * returns the cap silently, which would hide any utility past it.
 *
 * A single utility's full row (steps and all) comes from useUtilityDetail(),
 * cached per id for the life of the page so the three screens that show the
 * same utility do not each fetch it.
 */

const PAGE = 1000;

/** Enough to cover the worst ZIP (16) and the largest state (279) whole. */
const PICKER_LIMIT = 300;
/** A search list longer than this is a list nobody reads; refine the words instead. */
const SEARCH_LIMIT = 50;

export async function fetchAllUtilities(): Promise<UtilityListItem[]> {
  const all: UtilityListItem[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('utilities')
      .select(UTILITY_LIST_COLUMNS)
      .order('name', { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const rows = (data ?? []) as UtilityListItem[];
    all.push(...rows);
    if (rows.length < PAGE) break;
  }
  return all;
}

/**
 * The utilities serving a ZIP, through zip_coverage. One round trip: the
 * foreign key lets PostgREST embed the utility row on the coverage row.
 */
export async function fetchUtilitiesForZip(zip: string): Promise<UtilityListItem[]> {
  if (!/^\d{5}$/.test(zip)) return [];
  const { data, error } = await supabase
    .from('zip_coverage')
    .select(`utilities!inner(${UTILITY_LIST_COLUMNS})`)
    .eq('zip', zip)
    .limit(PICKER_LIMIT);
  if (error) throw error;
  const rows = (data ?? []) as unknown as Array<{ utilities: UtilityListItem | UtilityListItem[] | null }>;
  return rows.flatMap((r) => (Array.isArray(r.utilities) ? r.utilities : r.utilities ? [r.utilities] : []));
}

/** Every utility serving a state, by home state or by the served list. */
export async function fetchUtilitiesForState(state: string): Promise<UtilityListItem[]> {
  const code = state.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return [];
  const { data, error } = await supabase
    .from('utilities')
    .select(UTILITY_LIST_COLUMNS)
    .or(`state.eq.${code},states_served.cs.{${code}}`)
    .order('residential_customers', { ascending: false, nullsFirst: false })
    .limit(PICKER_LIMIT);
  if (error) throw error;
  return (data ?? []) as UtilityListItem[];
}

/**
 * Name search across the whole table. This is what keeps utilities.md's rule
 * true once the picker stops listing every row: every utility stays reachable,
 * by typing rather than by scrolling.
 */
export async function searchUtilitiesByName(
  term: string,
  limit: number = SEARCH_LIMIT
): Promise<UtilityListItem[]> {
  const q = term.trim();
  if (!q) return [];
  const { data, error } = await supabase
    .from('utilities')
    .select(UTILITY_LIST_COLUMNS)
    .ilike('name', `%${q}%`)
    .order('residential_customers', { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as UtilityListItem[];
}

const detailCache = new Map<string, UtilityRow | null>();

/** Drop a cached row after an admin edit so the next read sees the change. */
export function invalidateUtilityDetail(id?: string) {
  if (id) detailCache.delete(id);
  else detailCache.clear();
}

export async function fetchUtilityDetail(id: string): Promise<UtilityRow | null> {
  if (detailCache.has(id)) return detailCache.get(id) ?? null;
  const { data, error } = await supabase.from('utilities').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  const row = (data as UtilityRow | null) ?? null;
  detailCache.set(id, row);
  return row;
}

export interface UtilityDetailState {
  utility: UtilityRow | null;
  /** True while the row for a set id has not arrived. */
  loading: boolean;
}

export function useUtilityDetail(utilityId: string | null | undefined): UtilityDetailState {
  const [state, setState] = useState<UtilityDetailState>(() => ({
    utility: utilityId ? detailCache.get(utilityId) ?? null : null,
    loading: !!utilityId && !detailCache.has(utilityId),
  }));

  useEffect(() => {
    let cancelled = false;
    if (!utilityId) {
      setState({ utility: null, loading: false });
      return;
    }
    if (detailCache.has(utilityId)) {
      setState({ utility: detailCache.get(utilityId) ?? null, loading: false });
      return;
    }
    setState({ utility: null, loading: true });
    fetchUtilityDetail(utilityId)
      .then(row => {
        if (!cancelled) setState({ utility: row, loading: false });
      })
      .catch(err => {
        console.error('Error fetching utility:', err);
        // A failed read must not make the utility look unlisted for good; the
        // next mount retries because nothing was cached.
        if (!cancelled) setState({ utility: null, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [utilityId]);

  return state;
}
